import { getPriceFromContent } from '@/components/ui/PDP/helpers';
import { OtherLabels } from '@/hooks/PDP/useCatalogLabels';
import { ProgramVariation } from '@/hooks/PDP/useProgramCatalogRelations';
import { isOfContentType } from '@/lib/helpers/contentType';
import { format, parse } from 'date-fns';
import MD5 from 'crypto-js/md5';
import { CurrencySign } from '@/constants/currency';
import parseHTML, { HTMLReactParserOptions } from 'html-react-parser';
import { Optional } from './types';

export const DateFormat = 'MM/dd/yyyy';
export const BackendDateFormat = 'yyyy-MM-dd';

const siteBaseUrls = new Set<string>([
  'https://testfrontend.pli.edu',
  'https://stagingfrontend.pli.edu',
  'https://frontend.pli.edu',
  'https://testfrontdoor.pli.edu'
]);

export const parseDate = (value?: string, dateFormat = DateFormat): Date | undefined => {
  if (!value || value.length !== dateFormat.length) {
    return undefined;
  }
  const date = parse(value, dateFormat, new Date());
  const isValidDate = !isNaN(date.getTime());
  return isValidDate ? date : undefined;
};

export const formatNumber = (x: number) => {
  return x.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const parseBEDate = (value?: string): Date | undefined =>
  parseDate(value, BackendDateFormat);

export const formatDate = (date: Date, dateFormat = DateFormat): string => format(date, dateFormat);

export const formatBEDateToFEDate = (dateString?: string): string | undefined => {
  if (!dateString) return '';
  const date = parseBEDate(dateString);
  return date && formatDate(date);
};

export const formatString = (str?: string | null, ...args: string[]) => {
  if (!str || !args || args.length == 0) return str;

  return str.replace(/{(\d+)}/g, (_, index) => args[index] || '');
};

export const isExternalUrl = (url: string): boolean => {
  try {
    if (url == '') {
      return false;
    }
    const link = new URL(url);
    return link.origin !== window.location.origin;
  } catch (error) {
    return false;
  }
};

export const getDomainFromUrl = (url: string) => {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname;
  } catch (error) {
    return null;
  }
};

export const isRelativeUrl = (url: string): boolean => url.startsWith('/');

export const isPLIUrl = (url: string): boolean => {
  try {
    const link = new URL(url);
    return link.origin === window.location.origin || siteBaseUrls.has(link.origin);
  } catch (error) {
    return false;
  }
};

export const isRelativeOrPLIUrl = (url: string): boolean => isRelativeUrl(url) || isPLIUrl(url);

export const transformIfPLIUrl = (value?: string): string | undefined => {
  // If the link is in a PLI environment we can use the pathname
  if (value && isPLIUrl(value)) {
    try {
      const url = new URL(value);
      const transformedLink = url.pathname + url.search;
      return transformedLink;
    } catch {
      return value;
    }
  } else {
    return value;
  }
};

export const transformUrlToPath = (value?: string): string => {
  if (!value) return '/';
  return new URL(value, window.location.origin).pathname;
};

export const extractTextFromHTML = (htmlString: string) => {
  const tempElement = document.createElement('span');
  tempElement.innerHTML = htmlString;
  const simplifiedText = tempElement.textContent || tempElement.innerText || '';
  tempElement.remove();
  return simplifiedText;
};

export const addClassesToLinks = (element: Element) => {
  const children = element.children;

  if (children.length) {
    for (const c of children) {
      if (c.children?.length) addClassesToLinks(c);

      if (c.localName === 'a') {
        c.classList.add('text-link', 'underline');
      }
    }
  }
};

export const extractTextFromParagraph = (htmlString: string) => {
  if (htmlString?.trim().startsWith('<p>')) {
    const tempElement = document.createElement('span');
    tempElement.innerHTML = htmlString;
    const paragraph = tempElement.children?.[0];
    addClassesToLinks(paragraph);
    const simplifiedText = paragraph?.innerHTML ?? '';
    tempElement.remove();
    return simplifiedText;
  }
};

export const compareArrayIndex = <T, V>(
  array: T[],
  a: V,
  b: V,
  checkerFn: (listItem: T, checkItem: V) => boolean
): number => {
  const aIndex = array.findIndex(i => checkerFn(i, a));
  const bIndex = array.findIndex(i => checkerFn(i, b));
  if (aIndex === -1 || bIndex === -1) {
    return 0;
  }
  return aIndex - bIndex;
};

export const mergeURLSearchParams = (
  params1: URLSearchParams,
  params2: URLSearchParams
): URLSearchParams => {
  const mergedParams = new URLSearchParams(params1);

  for (const [key, value] of params2.entries()) {
    mergedParams.append(key, value);
  }

  return mergedParams;
};

export const unknownToBoolean = (value: unknown) => {
  if (typeof value === 'string') {
    const strValue = value?.toLowerCase();
    if (strValue == 'true') return true;
    else if (strValue == 'false') return false;
  }
  if (typeof value === 'boolean') return value;
  return !!value;
};

export const formatTcode = (sourceCode?: string, keyCode?: string) =>
  sourceCode && keyCode ? `${sourceCode}_${keyCode}` : undefined;

export const launchNotifyMePopup = (notifyMeUrl: string) =>
  window.open(notifyMeUrl, 'popup', 'width=740,height=366,left=62,top=88');

export const getAddToCartCTALabel = (
  variant: ProgramVariation,
  labelText: string,
  otherLabels?: OtherLabels
) => {
  if (!variant) {
    return undefined;
  }

  const price = getPriceFromContent(variant);
  if (
    isOfContentType(variant, [
      'IndividualMembershipVariation',
      'IndividualPlusSubscriptionVariation'
    ])
  ) {
    return otherLabels?.membershipCartText
      .replace('{ADD_TO_CART_CTA}', labelText)
      .replace('{PRICE}', price.price);
  }

  return `${labelText} • ${price.price}`;
};

export const simpleHash = (str: string): string => {
  const fullHash = MD5(str).toString();
  return fullHash.substring(0, 10);
};

export const isValidUrl = (string: string) => {
  try {
    new URL(string);
  } catch (_) {
    return false;
  }

  return true;
};

export const isLocal = (hostname: string = window.location.hostname) =>
  ['localhost', '127.0.0.1', '', '::1'].includes(hostname);

export const formatPrice = (price: number) => {
  return `${CurrencySign.USD}${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

type TemplateVariables = Record<string, string | number | undefined | null>;

export function templateString(template: string, variables: TemplateVariables): string;
export function templateString(
  template: string,
  variables: TemplateVariables,
  parseHtml: true | HTMLReactParserOptions
): React.ReactNode;
export function templateString(
  template: string,
  variables: TemplateVariables,
  parseHtml?: true | HTMLReactParserOptions
) {
  template = template.replace(/{(\w+)}/g, (match, key) => {
    return key in variables ? (variables[key]?.toString() ?? '') : match;
  });

  if (parseHtml) {
    template = template.replace(/\n/g, '<br/>');

    return parseHTML(template, typeof parseHtml === 'object' ? parseHtml : undefined);
  }

  return template;
}

export type ObjectText2Html<O extends object, Keys extends keyof O = keyof O> = Optional<
  {
    [K in keyof O]: [K, NonNullable<O[K]>] extends [Keys, string] ? React.ReactNode : O[K];
  },
  Extract<O, undefined>
>;

/**
 * Performs a recursive equality check for plain object trees.
 *
 * - Compares primitive values with strict equality.
 * - Compares arrays by length and element order.
 * - Recurses into nested objects.
 *
 * Note: array/object elements that are non-plain values (for example Date, Map, functions)
 * are compared by reference/equality semantics, not by custom deep normalization.
 */
export const compareObjects = <T extends Record<string, unknown>>(obj1: T, obj2: T): boolean => {
  if (obj1 === obj2) return true;

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  for (let i = 0; i < keys1.length; i++) {
    const key = keys1[i] as keyof T;

    if (!Object.prototype.hasOwnProperty.call(obj2, key)) return false;

    const value1 = obj1[key];
    const value2 = obj2[key];

    if (Array.isArray(value1) || Array.isArray(value2)) {
      if (!Array.isArray(value1) || !Array.isArray(value2)) return false;
      if (value1.length !== value2.length) return false;
      for (let j = 0; j < value1.length; j++) {
        if (value1[j] !== value2[j]) return false;
      }
      continue;
    }

    if (value1 && value2 && typeof value1 === 'object' && typeof value2 === 'object') {
      if (!compareObjects(value1 as Record<string, unknown>, value2 as Record<string, unknown>)) {
        return false;
      }
      continue;
    }

    if (value1 !== value2) return false;
  }

  return true;
};
